import { useMutation, useQueryClient } from '@tanstack/react-query';
import { authedFetch } from './platformTenants';
import type { Role } from './adminUsers';

// ─── Warehouse-role grant/revoke/set-default (SP-5h) ─────────────────────────

export type WarehouseRole = 'warehouse-admin' | 'voice-supervisor';

export interface UserMembership {
  id: number;
  warehouse_id: number;
  warehouse_code: string;
  warehouse_name: string;
  role: WarehouseRole;
  is_default: boolean;
}

function tenantWarehouseHeaders(tenantCode: string | null): Record<string, string> {
  const h: Record<string, string> = {};
  if (tenantCode) h['X-Tenant-Id'] = tenantCode;
  return h;
}

function tenantHeaders(tenantCode: string | null): Record<string, string> {
  return tenantCode ? { 'X-Tenant-Id': tenantCode } : {};
}

export function useCreateMembership(token: string | null, tenantCode: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      body: { user_id: number; role: Role; warehouse_id?: number | null };
      idempotencyKey: string;
    }) => {
      const headers = tenantHeaders(tenantCode);
      headers['Idempotency-Key'] = vars.idempotencyKey;
      return await authedFetch('/admin/memberships', token, {
        method: 'POST',
        body: JSON.stringify(vars.body),
        headers,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export function useUpdateMembership(token: string | null, tenantCode: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: number; patch: { role?: Role; warehouse_id?: number | null } }) =>
      await authedFetch(`/admin/memberships/${vars.id}`, token, {
        method: 'PATCH',
        body: JSON.stringify(vars.patch),
        headers: tenantHeaders(tenantCode),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export function useDeleteMembership(token: string | null, tenantCode: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) =>
      await authedFetch(`/admin/memberships/${id}`, token, {
        method: 'DELETE',
        headers: tenantHeaders(tenantCode),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export function useGrantWarehouseRole(token: string | null, tenantCode: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      userId: number;
      warehouseId: number;
      role: WarehouseRole;
      isDefault: boolean;
    }) =>
      await authedFetch('/admin/memberships/grant-warehouse-role', token, {
        method: 'POST',
        body: JSON.stringify({
          user_id: vars.userId,
          warehouse_id: vars.warehouseId,
          role: vars.role,
          is_default: vars.isDefault,
        }),
        headers: {
          ...tenantWarehouseHeaders(tenantCode),
          'Idempotency-Key': crypto.randomUUID(),
        },
      }),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      qc.invalidateQueries({ queryKey: ['admin', 'memberships'] });
    },
  });
}

export function useRevokeWarehouseRole(token: string | null, tenantCode: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { membershipId: number }) =>
      await authedFetch(`/admin/memberships/${vars.membershipId}/revoke-warehouse-role`, token, {
        method: 'POST',
        body: JSON.stringify({}),
        headers: {
          ...tenantWarehouseHeaders(tenantCode),
          'Idempotency-Key': crypto.randomUUID(),
        },
      }),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      qc.invalidateQueries({ queryKey: ['admin', 'memberships'] });
    },
  });
}

export function useSetDefaultWarehouse(token: string | null, tenantCode: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      userId: number;
      warehouseId: number;
      role: WarehouseRole;
    }) =>
      await authedFetch('/admin/memberships/set-default-warehouse', token, {
        method: 'POST',
        body: JSON.stringify({
          user_id: vars.userId,
          warehouse_id: vars.warehouseId,
          role: vars.role,
        }),
        headers: {
          ...tenantWarehouseHeaders(tenantCode),
          'Idempotency-Key': crypto.randomUUID(),
        },
      }),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      qc.invalidateQueries({ queryKey: ['admin', 'memberships'] });
    },
  });
}
